import { Component, startComponents, stopComponents, findComponents } from '@gondel/core';
import { GondelJqueryComponent } from '@gondel/plugin-jquery';
import { Set } from 'core-js/es6';
import { Video } from '../../../molecules/video/js/video';

enum States {
	ConsentVisible = 'state-o-onetrust-wrapper__consent--visible',
	DeclinedHidden = 'state-o-onetrust-wrapper__declined--hidden',
}

enum Selectors {
	Consent = '.js-o-onetrust-wrapper__consent',
	Declined = '.js-o-onetrust-wrapper__declined',
	MoodVideo = '.js-o-onetrust-wrapper__consent > .js-m-video',
}

@Component('OnetrustWrapper', 't')
export class OnetrustWrapper extends GondelJqueryComponent {
	private $wrappedToggleCandidate: JQuery<HTMLElement>;
	private $consentPart: JQuery<HTMLElement>;
	private $declinedPart: JQuery<HTMLElement>;
	private $moodVideo: JQuery<HTMLElement>;
	private consentCategories: string[] = [];
	private isGondelChildBlocked: boolean;
	private gondelAttr: string = 'data-g-name';
	private disabledAttr: string = 'data-blocked-name';
	private blockedPatternName: string;

	public start() {
		this.isGondelChildBlocked = false;
		this.$consentPart = this.$ctx.find(`${Selectors.Consent}`);
		this.$declinedPart = this.$ctx.find(`${Selectors.Declined}`);
		this.consentCategories = this.$ctx.data('consent').split(' ');
		this.$wrappedToggleCandidate = this.$consentPart.find(`[${this.gondelAttr}]`).first();
		this.$moodVideo = this.$ctx.find(`${Selectors.MoodVideo}`);

		if (this.$wrappedToggleCandidate.length === 0) {
			return;
		}

		if (this.isNecessaryConsentAlreadyGiven()) {
			this.unblockWrappedCandidate(this.$wrappedToggleCandidate);
		} else {
			this.blockWrappedCandidate(this.$wrappedToggleCandidate);
		}

		window.addEventListener('consent.onetrust', (ev: CustomEvent) => this.handleOneTrustEvent(ev), false);
	}

	public stop() {
		window.removeEventListener('consent.onetrust', (ev: CustomEvent) => this.handleOneTrustEvent(ev), false);
	}

	private isNecessaryConsentAlreadyGiven() {
		// tslint:disable-next-line: no-string-literal
		const oneTrustConsents: string = window['OnetrustActiveGroups'];
		const consentsGiven = oneTrustConsents
			? oneTrustConsents
					.replace(/,/g, ' ')
					.trim()
					.split(' ')
			: [];

		if (consentsGiven.length === 0) {
			return false;
		}

		return this.hasAllNecessaryConsents(consentsGiven);
	}

	private handleOneTrustEvent(ev: CustomEvent) {
		if (!ev.detail || this.$wrappedToggleCandidate.length === 0) {
			return;
		}

		if (this.hasAllNecessaryConsents(ev.detail)) {
			this.unblockWrappedCandidate(this.$wrappedToggleCandidate);
		} else {
			this.blockWrappedCandidate(this.$wrappedToggleCandidate);
		}
	}

	private hasAllNecessaryConsents(consentedCategories: string[]) {
		const consentedCategoriesWithoutDuplicates = this.noDuplicates(consentedCategories);
		return this.compareArrays(this.consentCategories, consentedCategoriesWithoutDuplicates);
	}

	private noDuplicates(items: string[]) {
		return Array.from(new Set(items).values()); // keep IE11 compatible!
	}

	private compareArrays(a1: string[], a2: string[]) {
		const result = a1.every((x) => {
			return a2.indexOf(x) !== -1; // keep IE11 compatible!
		});
		return result;
	}

	private blockWrappedCandidate($item: JQuery) {
		this.updateBlockedPatternName($item);
		// hide component / show consent info
		this.$consentPart.toggleClass(States.ConsentVisible, false);
		this.$declinedPart.toggleClass(States.DeclinedHidden, false);
		stopComponents(this.$consentPart[0]);
		this.isGondelChildBlocked = true;
		this.removeGondelNameAttr($item);
	}

	private unblockWrappedCandidate($item: JQuery) {
		this.updateBlockedPatternName($item);
		this.addGondelNameAttr($item);
		// hide consent info / show component
		this.$declinedPart.toggleClass(States.DeclinedHidden, true);
		this.$consentPart.toggleClass(States.ConsentVisible, true);
		startComponents(this.$consentPart[0]);
		// video (no start/sync) needs manual init, only for mood-image
		this.manuallyInitVideo();
		this.isGondelChildBlocked = false;
	}

	private updateBlockedPatternName($item: JQuery) {
		this.blockedPatternName =
			(this.isGondelChildBlocked ? $item.attr(this.disabledAttr) : $item.attr(this.gondelAttr)) || '';
	}

	private manuallyInitVideo() {
		if (this.$moodVideo.length <= 0) {
			return;
		}
		const videoComponents = findComponents<Video>(this.$consentPart, 'Video', 'g');
		if (videoComponents.length <= 0) {
			return;
		}
		if (videoComponents[0] !== null) {
			videoComponents[0].initVideoPlayer();
		}
	}

	// remove data-g-name and add data-disabled-name attr on target component
	private removeGondelNameAttr($item: JQuery) {
		if ($item[0].hasAttribute(this.gondelAttr)) {
			$item.removeAttr(this.gondelAttr);
			$item.attr(this.disabledAttr, this.blockedPatternName);
		}
	}

	// add data-g-name and remove data-disabled-name attr on target component
	private addGondelNameAttr($item: JQuery) {
		if (this.isGondelChildBlocked && $item[0].hasAttribute(this.disabledAttr)) {
			$item.attr(this.disabledAttr, '');
			$item.attr(this.gondelAttr, this.blockedPatternName);
		}
	}
}
